import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type DropdownProps } from 'react-day-picker';
import { cn } from '@/lib/utils/cn';
import { buttonVariants } from './button';
import { Select, SelectContent, SelectItem, SelectTrigger } from './select';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Month / year dropdown of the calendar caption, drawn with the app's Select instead of
 * the native <select>. react-day-picker passes the choices as <option> children.
 */
function CalendarDropdown({ value, onChange, children, caption, 'aria-label': ariaLabel }: DropdownProps) {
  const options = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement<
    React.OptionHTMLAttributes<HTMLOptionElement>
  >[];
  return (
    <Select
      value={String(value)}
      onValueChange={(next) => onChange?.({ target: { value: next } } as React.ChangeEvent<HTMLSelectElement>)}
    >
      <SelectTrigger aria-label={ariaLabel} className="h-7 w-auto gap-1 px-2.5 text-sm font-semibold">
        <span>{caption}</span>
      </SelectTrigger>
      {/* Above the calendar popover, also when the calendar is inside a modal. */}
      <SelectContent className="z-[2200] max-h-64">
        {options.map((option) => (
          <SelectItem key={String(option.props.value)} value={String(option.props.value)}>
            {option.props.children}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Year range of the month / year dropdowns when the caller sets no limit on that side.
const YEARS_BACK = 75;
const YEARS_AHEAD = 25;

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown-buttons',
  ...props
}: CalendarProps) {
  // react-day-picker v8 only shows the dropdowns when both ends of the range are known:
  // keep the caller's min / max and fill a wide default for a missing side.
  const thisYear = new Date().getFullYear();
  const hasStart = props.fromDate || props.fromMonth || props.fromYear;
  const hasEnd = props.toDate || props.toMonth || props.toYear;
  const yearBounds = {
    ...(hasStart ? {} : { fromYear: thisYear - YEARS_BACK }),
    ...(hasEnd ? {} : { toYear: thisYear + YEARS_AHEAD }),
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      {...yearBounds}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'flex items-center text-sm font-medium',
        // Month / year dropdowns (CalendarDropdown); the hidden caption text is for screen readers.
        caption_dropdowns: 'flex items-center justify-center gap-1.5',
        vhidden: 'sr-only',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'h-8 w-8 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: cn(buttonVariants({ variant: 'ghost' }), 'h-8 w-8 p-0 font-normal aria-selected:opacity-100'),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
        Dropdown: CalendarDropdown,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';
